// src/generators/dzial9_prawdopodobienstwo/temat4_zadania_uzupelniajace_maturalne/podtemat3_otwarte_types/MaturaTwoDiceNumberOddDiv3OpenTask.js

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";
import OpenTaskScoringHint from "../../../../components/ui/OpenTaskScoringHint";
import ProbabilityOutcomeTable from "../../../../components/ui/ProbabilityOutcomeTable";


/* ===== ułamki ===== */

function gcd(a, b) {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) {
    const t = b;
    b = a % b;
    a = t;
  }
  return a || 1;
}

function simplifyFraction(num, den) {
  if (den === 0) return [num, den];
  const sign = den < 0 ? -1 : 1;
  const d = gcd(num, den);
  return [sign * (num / d), sign * (den / d)];
}

function parseFraction(raw) {
  if (raw == null) return null;
  const s = String(raw).replace(",", ".").trim();
  if (!s) return null;

  // całkowita
  if (/^[+-]?\d+$/.test(s)) {
    return [parseInt(s, 10), 1];
  }

  // a/b
  const m = s.match(/^\s*([+-]?\d+)\s*\/\s*([+-]?\d+)\s*$/);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  const d = parseInt(m[2], 10);
  if (!Number.isFinite(n) || !Number.isFinite(d) || d === 0) return null;
  return simplifyFraction(n, d);
}

function dfrac(n, d) {
  const [nn, dd] = simplifyFraction(n, d);
  return dd === 1 ? `${nn}` : `\\dfrac{${nn}}{${dd}}`;
}

function formatProbLatex(frac) {
  if (!frac) return "";
  const [n, d] = frac;
  return `P(A) = ${dfrac(n, d)}`;
}

/* ===== tabela wyników (liczba / suma / iloczyn) ===== */

function OutcomesTable({ favourableSet, displayMode }) {
  return (
    <ProbabilityOutcomeTable
      rows={[1, 2, 3, 4, 5, 6]}
      columns={[1, 2, 3, 4, 5, 6]}
      getCellValue={(a, b) => {
        if (displayMode === "sum") return a + b;
        if (displayMode === "prod") return a * b;
        return 10 * a + b;
      }}
      isHighlighted={(a, b) => favourableSet.has(`${a}-${b}`)}
      ariaLabel="Tabela wyników dwóch rzutów kostką"
    />
  );
}

/* ===== generowanie wariantów ===== */

function buildAllOutcomes() {
  const all = [];
  for (let a = 1; a <= 6; a += 1) {
    for (let b = 1; b <= 6; b += 1) {
      const value = 10 * a + b;
      const sum = a + b;
      const prod = a * b;
      all.push({ a, b, value, sum, prod });
    }
  }
  return all;
}

// warianty podzielności (liczba dwucyfrowa w kratkach)
const DIV_VARIANTS = [
  {
    code: "odd_div3",
    isFav: (o) => o.value % 2 === 1 && o.value % 3 === 0,
    // opis cechy, bez "powstała liczba dwucyfrowa"
    text: "nieparzysta i podzielna przez ",
    latex: "10a + b \\text{ jest nieparzysta i podzielna przez } 3",
  },
  {
    code: "even_div3",
    isFav: (o) => o.value % 2 === 0 && o.value % 3 === 0,
    text: "parzysta i podzielna przez ",
    latex: "10a + b \\text{ jest parzysta i podzielna przez } 3",
  },
  {
    code: "div15",
    isFav: (o) => o.value % 15 === 0,
    text: "podzielna przez ",
    latex: "10a + b \\text{ jest podzielna przez } 15",
  },
];

// relacje dla sumy/iloczynu
const REL_MODES = [
  { key: "gt", phrase: "większa niż", latexRel: ">" },
  { key: "lt", phrase: "mniejsza niż", latexRel: "<" },
  { key: "ge", phrase: "nie mniejsza niż", latexRel: "\\ge" },
  { key: "le", phrase: "nie większa niż", latexRel: "\\le" },
];

function randomChoice(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

// wariant podzielności (liczba dwucyfrowa)
function generateDivVariant(all) {
  const variant = randomChoice(DIV_VARIANTS);
  const favourable = all.filter(variant.isFav);
  const favCount = favourable.length;

  if (favCount === 0) {
    return generateDivVariant(all);
  }

  return {
    conditionType: "number", // w kratkach liczby dwucyfrowe
    variantCode: variant.code,
    relKey: null,
    threshold: null,
    displayMode: "number",
    descriptionText: variant.text,
    latexCondition: variant.latex,
    favourable,
    favCount,
  };
}

function generateSumProdVariant(all, kind) {
  // kind: "sum" albo "prod"
  const mode = randomChoice(REL_MODES);

  const sumThresholds = [3, 4, 5, 6, 7, 8, 9, 10, 11];
  const prodThresholds = [2, 3, 4, 5, 6, 8, 9, 10, 12, 15, 16, 18, 20];

  const candidates = kind === "sum" ? sumThresholds : prodThresholds;

  for (let tries = 0; tries < 20; tries += 1) {
    const threshold = randomChoice(candidates);

    const isFav =
      kind === "sum"
        ? (o) => {
            const s = o.sum;
            if (mode.key === "gt") return s > threshold;
            if (mode.key === "lt") return s < threshold;
            if (mode.key === "ge") return s >= threshold;
            if (mode.key === "le") return s <= threshold;
            return false;
          }
        : (o) => {
            const p = o.prod;
            if (mode.key === "gt") return p > threshold;
            if (mode.key === "lt") return p < threshold;
            if (mode.key === "ge") return p >= threshold;
            if (mode.key === "le") return p <= threshold;
            return false;
          };

    const favourable = all.filter(isFav);
    const favCount = favourable.length;

    if (favCount > 0 && favCount < all.length) {
      const textWhat =
        kind === "sum"
          ? "suma liczb wyrzuconych oczek"
          : "iloczyn liczb wyrzuconych oczek";
      const latexLeft = kind === "sum" ? "a + b" : "a \\cdot b";
      const latexCondition = `${latexLeft} ${mode.latexRel} ${threshold}`;

      return {
        conditionType: kind, // "sum" / "prod"
        variantCode: null,
        relKey: mode.key,
        threshold,
        displayMode: kind, // decyduje, co wpisujemy w kratkach
        descriptionText: `${textWhat} jest ${mode.phrase} ${threshold}`,
        latexCondition,
        favourable,
        favCount,
      };
    }
  }

  return null;
}

function generateOne() {
  const all = buildAllOutcomes();

  const groups = ["number", "sum", "prod"];
  const group = randomChoice(groups);

  let variantInfo = null;

  if (group === "number") {
    variantInfo = generateDivVariant(all);
  } else if (group === "sum") {
    variantInfo = generateSumProdVariant(all, "sum");
  } else {
    variantInfo = generateSumProdVariant(all, "prod");
  }

  if (!variantInfo) {
    variantInfo = generateDivVariant(all);
  }

  const { favourable, favCount } = variantInfo;
  const omegaSize = all.length; // 36
  const correctFraction = simplifyFraction(favCount, omegaSize);

  // UJEDNOLICENIE: wszystko pakujemy do data
  return {
    type: "MaturaTwoDiceNumberOddDiv3OpenTask",
    data: {
      maxPoints: 2,
      omegaSize,
      correctFraction,
      favourable,
      favCount,
      conditionType: variantInfo.conditionType, // "number" | "sum" | "prod"
      variantCode: variantInfo.variantCode,
      relKey: variantInfo.relKey,
      threshold: variantInfo.threshold,
      displayMode: variantInfo.displayMode,
      descriptionText: variantInfo.descriptionText,
      latexCondition: variantInfo.latexCondition,
    },
  };
}

/* ===== API ===== */

function createOriginalTask() {
  const all = buildAllOutcomes();
  const favourable = all.filter(
    (outcome) => outcome.value % 2 === 1 && outcome.value % 3 === 0
  );

  return {
    type: "MaturaTwoDiceNumberOddDiv3OpenTask",
    data: {
      maxPoints: 2,
      omegaSize: all.length,
      correctFraction: simplifyFraction(favourable.length, all.length),
      favourable,
      favCount: favourable.length,
      conditionType: "number",
      variantCode: "odd_div3",
      relKey: null,
      threshold: null,
      displayMode: "number",
      descriptionText: "nieparzysta i podzielna przez ",
      latexCondition:
        "10a + b \\text{ jest nieparzysta i podzielna przez } 3",
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_SIE",
      taskNumber: 29,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") {
    return createOriginalTask();
  }

  return generateOne();
}

/* ===== treść zadania ===== */

function renderConditionSentence(data) {
  const { conditionType, relKey, threshold, descriptionText, variantCode } =
    data;

  // warianty typu "suma liczb..." / "iloczyn liczb..."
  if (conditionType === "sum" || conditionType === "prod") {
    const what =
      conditionType === "sum"
        ? "suma liczb wyrzuconych oczek"
        : "iloczyn liczb wyrzuconych oczek";

    const mode = REL_MODES.find((m) => m.key === relKey);
    const phrase = mode ? mode.phrase : "";

    if (threshold != null) {
      return (
        <>
          {what} jest {phrase} <InlineMath math={String(threshold)} />
        </>
      );
    }

    return `${what} jest ${phrase}`;
  }

  // warianty "number" – cecha samej liczby dwucyfrowej
  if (conditionType === "number") {
    if (variantCode === "odd_div3" || variantCode === "even_div3") {
      return (
        <>
          jest {descriptionText}
          <InlineMath math={"3"} />
        </>
      );
    }

    if (variantCode === "div15") {
      return (
        <>
          jest {descriptionText}
          <InlineMath math={"15"} />
        </>
      );
    }

    return <>jest {descriptionText}</>;
  }

  return descriptionText;
}

export function renderTask({ data }) {
  const isNumberVariant = data.conditionType === "number";

  return (
    <>
      <p>
        Doświadczenie losowe polega na dwukrotnym rzucie symetryczną
        sześcienną kostką do gry. Zapisujemy kolejno liczby wyrzuconych oczek
        i w ten sposób otrzymujemy liczbę dwucyfrową: pierwsza liczba oczek
        jest cyfrą dziesiątek, a druga – cyfrą jedności tej liczby.
      </p>

      {isNumberVariant ? (
        <p>
          Oblicz prawdopodobieństwo zdarzenia <InlineMath math={"A"} />,
          polegającego na tym, że powstała liczba dwucyfrowa{" "}
          {renderConditionSentence(data)}. Zapisz obliczenia.
        </p>
      ) : (
        <p>
          Oblicz prawdopodobieństwo zdarzenia <InlineMath math={"A"} />,
          polegającego na tym, że{" "}
          {renderConditionSentence(data)}. Zapisz obliczenia.
        </p>
      )}
    </>
  );
}

/* ===== INPUT: 3 pola (FractionInput) ===== */

export function renderInput(_task, userAnswer, onChange, disabled) {
  const ua = userAnswer || {};
  const setField = (key) => (val) => onChange({ ...(ua || {}), [key]: val });

  return (
    <div style={{ display: "grid", rowGap: 12, maxWidth: 340 }}>
      <OpenTaskScoringHint variant="independent" />
      {/* |Ω| */}
      <div style={{ display: "grid", rowGap: 4 }}>
        <InlineMath math={"|\\Omega| ="} />
        <FractionInput
          value={ua.omega}
          onChange={setField("omega")}
          disabled={disabled}
          label="|\Omega|"
          placeholder="np. 36"
        />
      </div>

      {/* |A| */}
      <div style={{ display: "grid", rowGap: 4 }}>
        <InlineMath math={"|A| ="} />
        <FractionInput
          value={ua.A}
          onChange={setField("A")}
          disabled={disabled}
          label="|A|"
          placeholder="np. 10"
        />
      </div>

      {/* P(A) */}
      <div style={{ display: "grid", rowGap: 4 }}>
        <InlineMath math={"P(A) ="} />
        <FractionInput
          value={ua.prob}
          onChange={setField("prob")}
          disabled={disabled}
          label="P(A)"
          placeholder="np. 5/18"
        />
      </div>
    </div>
  );
}

/* ===== sprawdzanie (0 / 1 / 2 pkt) ===== */

function fieldFractionEqualsInt(fieldVal, targetInt) {
  let parsed = null;

  if (fieldVal && typeof fieldVal === "object" && fieldVal.raw != null) {
    parsed = parseFraction(fieldVal.raw);
  } else if (
    typeof fieldVal === "string" ||
    typeof fieldVal === "number"
  ) {
    parsed = parseFraction(fieldVal);
  }

  if (!parsed) return false;
  const [n, d] = simplifyFraction(parsed[0], parsed[1]);
  return d === 1 && n === targetInt;
}

function fieldFractionEqualsFrac(fieldVal, targetFrac) {
  let parsed = null;

  if (fieldVal && typeof fieldVal === "object" && fieldVal.raw != null) {
    parsed = parseFraction(fieldVal.raw);
  } else if (
    typeof fieldVal === "string" ||
    typeof fieldVal === "number"
  ) {
    parsed = parseFraction(fieldVal);
  }

  if (!parsed) return false;
  const [n1, d1] = simplifyFraction(parsed[0], parsed[1]);
  const [n2, d2] = simplifyFraction(targetFrac[0], targetFrac[1]);
  return n1 === n2 && d1 === d2;
}

export function checkAnswer({ data }, userAnswer) {
  const ua = userAnswer || {};
  const { omegaSize, favCount, correctFraction } = data;

  const omegaOk = fieldFractionEqualsInt(ua.omega, omegaSize);
  const aOk = fieldFractionEqualsInt(ua.A, favCount);
  const probOk = fieldFractionEqualsFrac(ua.prob, correctFraction);

  const correctCount = [omegaOk, aOk, probOk].filter(Boolean).length;
  const score = correctCount === 3 ? 2 : correctCount > 0 ? 1 : 0;

  return {
    isCorrect: omegaOk && aOk && probOk,
    correct: omegaOk && aOk && probOk,
    score,
    maxScore: 2,
    parts: {
      omega: omegaOk,
      A: aOk,
      prob: probOk,
    },
    expected: {
      omega: String(omegaSize),
      A: String(favCount),
      prob: formatProbLatex(correctFraction),
    },
  };
}

/* ===== odpowiedź modelowa (krótka) ===== */

export function renderAnswer({ data }) {
  const { omegaSize, favCount, correctFraction } = data;
  const [n, d] = correctFraction;

  return (
    <div style={{ display: "grid", rowGap: 4, marginTop: 12 }}>
      <InlineMath math={`|\\Omega| = ${omegaSize}`} />
      <InlineMath math={`|A| = ${favCount}`} />
      <InlineMath math={`P(A) = ${dfrac(n, d)}`} />
    </div>
  );
}

/* ===== odpowiedź użytkownika ===== */

export function renderUserAnswer(userAnswer) {
  if (!userAnswer) return <i>(brak odpowiedzi)</i>;

  const getLatex = (raw) => {
    if (!raw) return "\\text{--}";

    const parsed = parseFraction(raw);
    if (parsed) {
      const [n, d] = parsed;
      return dfrac(n, d);
    }

    if (/^[+-]?\d+$/.test(String(raw).trim())) {
      return String(raw).trim();
    }

    return String(raw);
  };

  const omegaLatex = getLatex(userAnswer.omega?.raw ?? userAnswer.omega);
  const ALatex = getLatex(userAnswer.A?.raw ?? userAnswer.A);
  const probLatex = getLatex(userAnswer.prob?.raw ?? userAnswer.prob);

  return (
    <div style={{ display: "grid", rowGap: 4, marginTop: 12 }}>
      <InlineMath math={`|\\Omega| = ${omegaLatex}`} />
      <InlineMath math={`|A| = ${ALatex}`} />
      <InlineMath math={`P(A) = ${probLatex}`} />
    </div>
  );
}

/* ===== wyjaśnienie – wersja bez wypisywania A ===== */

export function renderExplanation({ data }) {
  const {
    omegaSize,
    favCount,
    correctFraction,
    favourable,
    displayMode,
    conditionType,
    latexCondition,
  } = data;

  const favourableSet = new Set(
    favourable.map(({ a, b }) => `${a}-${b}`)
  );

  const [n, d] = correctFraction;
  const mode =
    displayMode === "sum"
      ? "sum"
      : displayMode === "prod"
      ? "prod"
      : "number";

  const isNumberVariant = conditionType === "number";
  const whatSumProd =
    conditionType === "sum"
      ? "suma liczb wyrzuconych oczek"
      : conditionType === "prod"
      ? "iloczyn liczb wyrzuconych oczek"
      : null;

  const opisZaznaczania = isNumberVariant
    ? "Z tabeli zaznaczamy wszystkie liczby dwucyfrowe spełniające warunek."
    : `Z tabeli zaznaczamy wszystkie liczby dwucyfrowe, dla których ${whatSumProd} spełnia warunek.`;

  return (
    <>
      <p>
        <strong>Krok 1.</strong> Zapisz przestrzeń zdarzeń.
      </p>
      <p>
        Rzucamy dwa razy tę samą kostką, więc wszystkich możliwych
        uporządkowanych par <InlineMath math={"(a,b)"} /> (pierwszy i drugi
        rzut) jest <InlineMath math={"6 \\cdot 6 = 36"} />. Każdej parze{" "}
        <InlineMath math={"(a,b)"} /> odpowiada liczba dwucyfrowa{" "}
        <InlineMath math={"10a + b"} />.
      </p>

      <p>Rozpiszmy wyniki w tabeli:</p>
      <OutcomesTable favourableSet={favourableSet} displayMode={mode} />

      <p>
        <strong>Krok 2.</strong> Zapisz zdarzenie{" "}
        <InlineMath math={"A"} />.
      </p>

      {latexCondition && (
        <BlockMath math={`\\text{Warunek: } ${latexCondition}`} />
      )}

      <p>
        {opisZaznaczania} Z tabeli odczytujemy liczbę sprzyjających
        wyników:
      </p>
      <BlockMath
        math={`|A| = ${favCount}.`}
      />

      <p>
        Wszystkich możliwych wyników jest{" "}
        <InlineMath math={String(omegaSize)} />, więc{" "}
        <InlineMath math={"|\\Omega| = "} />
        <InlineMath math={String(omegaSize)} />.
      </p>

      <p>
        <strong>Krok 3.</strong> Oblicz prawdopodobieństwo zdarzenia{" "}
        <InlineMath math={"A"} />.
      </p>
      <BlockMath
        math={`P(A) = \\dfrac{|A|}{|\\Omega|} = \\dfrac{${favCount}}{${omegaSize}}`}
      />
      <BlockMath math={`P(A) = ${dfrac(n, d)}`} />

      <p>
        Zatem prawdopodobieństwo zdarzenia{" "}
        <InlineMath math={"A"} /> jest równe{" "}
        <InlineMath math={dfrac(n, d)} />.
      </p>
    </>
  );
}
